import { formatStringToDate } from '@/helpers/formatters';
import { getCurrentUrl, getRawUrl } from '@/helpers/location';
import { TrackingEventNameEnum } from '@/helpers/tracking';
import { MicroserviceEnum, Mutation } from '@/helpers/westeros';
import {
  trackEvent,
  TrackEventResult,
  TrackingEvent,
  TrackingEventInput,
  TrackingIdentify,
} from '@/store/graphql/mutations/westerosArcaneumMutations';
import RootState from '@/store/root_state';
import { ActionTree } from 'vuex';
import { PersonalData } from '../studentData/state';

type AnalyticsActionFactory = (mutation: Mutation<TrackEventResult>) => ActionTree<{}, RootState>;

export const userInfo = (studentPersonalData?: PersonalData): TrackingIdentify => {
  if (!studentPersonalData) return {};
  const { email, fullName: name } = studentPersonalData;
  return { id: email, email, name };
};

export const AnalyticsActions = {
  LOGIN_PLATFORM: 'LOGIN_PLATFORM',
  ENROLLMENT_STARTED: 'ENROLLMENT_STARTED',
  ENROLLMENT_DATA_SENT: 'ENROLLMENT_DATA_SENT',
  ENROLLMENT_DOCUMENTS_SENT: 'ENROLLMENT_DOCUMENTS_SENT',
  ENROLLMENT_CONTRACT_ACCEPTED: 'ENROLLMENT_CONTRACT_ACCEPTED',
  CHECKOUT_VIEWED: 'CHECKOUT_VIEWED',
  PAYMENT_SENT: 'PAYMENT_SENT',
  PAYMENT_CONFIRMED: 'PAYMENT_CONFIRMED',
  PAGE_VIEW: 'PAGE_VIEW',

  TRACK_EVENT: 'TRACK_EVENT',
  COMPLEMENTARY_ACCESS: 'COMPLEMENTARY_ACCESS',
};

const actionsFactory: AnalyticsActionFactory = mutation => ({
  async TRACK_EVENT(_, payload: TrackingEvent): Promise<void> {
    const eventInput: TrackingEventInput = {
      data: {
        ...payload,
        eventData: {
          $current_url: getCurrentUrl(),
          ...window.mixpanel?.cookie?.props,
          ...payload.eventData,
        },
      },
    };

    const { data } = await mutation(MicroserviceEnum.ARCANEUM, trackEvent, eventInput);

    if (!data?.trackEvent.success) {
      throw new Error('Tracking Service unable to track event');
    }
  },

  async LOGIN_PLATFORM({ dispatch, rootGetters }) {
    const { email } = rootGetters.personalData;

    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.LOGIN_PLATFORM,
      eventData: {
        email,
        email_processoseletivo: email,
        email_plataforma: email,
      },
      identify: userInfo(rootGetters.personalData),
    });
  },

  async ENROLLMENT_STARTED({ dispatch, rootGetters }) {
    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.ENROLLMENT_STARTED,
      eventData: {},
      identify: userInfo(rootGetters.personalData),
    });
  },

  async COMPLEMENTARY_ACCESS({ dispatch, rootGetters }, page: string) {
    const { personalData: student } = rootGetters;
    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.COMPLEMENTARY_ACCESS,
      eventData: {
        page,
      },
      identify: userInfo(student),
    });
  },

  async ENROLLMENT_DATA_SENT({ dispatch, rootGetters }) {
    const { personalData: student, studentAddress, partnerCode } = rootGetters;

    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.ENROLLMENT_DATA_SENT,
      eventData: {
        email: student?.email,
        cep: studentAddress?.zipCode,
        grad_endereco: studentAddress?.street,
        grad_endereco_numero: studentAddress?.number,
        grad_endereco_complemento: studentAddress?.complement,
        grad_endereco_cidade: studentAddress?.neighborhood,
        grad_endereco_estado: studentAddress?.state,
        data_de_nascimento: student?.birthDate
          ? formatStringToDate(student?.birthDate)?.toISOString()
          : undefined,
        grad_raca: student?.race,
        cpf: student?.cpf,
        grad_cupom: partnerCode,
        partnerCoupon: partnerCode,
      },
      identify: userInfo(student),
    });
  },

  async ENROLLMENT_DOCUMENTS_SENT({ dispatch, rootGetters }) {
    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.ENROLLMENT_DOCUMENTS_SENT,
      eventData: {},
      identify: userInfo(rootGetters.personalData),
    });
  },

  async ENROLLMENT_CONTRACT_ACCEPTED({ dispatch, rootGetters }) {
    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.ENROLLMENT_CONTRACT_ACCEPTED,
      eventData: {},
      identify: userInfo(rootGetters.personalData),
    });
  },

  async CHECKOUT_VIEWED({ dispatch, rootGetters }) {
    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.CHECKOUT_VIEWED,
      eventData: {},
      identify: userInfo(rootGetters.personalData),
    });
  },

  async PAYMENT_SENT({ dispatch, rootGetters }, payload) {
    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.PAYMENT_SENT,
      eventData: payload,
      identify: userInfo(rootGetters.personalData),
    });
  },

  async PAYMENT_CONFIRMED({ dispatch, rootGetters }, payload) {
    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.PAYMENT_CONFIRMED,
      eventData: payload,
      identify: userInfo(rootGetters.personalData),
    });
  },

  async PAGE_VIEW({ dispatch, rootGetters }) {
    dispatch(AnalyticsActions.TRACK_EVENT, {
      eventName: TrackingEventNameEnum.PAGE_VIEW,
      eventData: {
        gt_Location: getRawUrl(),
      },
      identify: userInfo(rootGetters.personalData),
    });
  },
});

export default actionsFactory;
